// frontend/src/pages/ProjectResults.tsx
import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatNumber, formatPercent } from "@/utils/format";
import { useProjectFormStore } from "@/store/project-form-provider";
import { useProjectResults } from "@/utils/use-project-results";

export default function ProjectResults() {
  const { project, loading, error, formData } = useProjectFormStore();

  const { id } = useParams();
  const { results } = useProjectResults(id);

  const { t } = useTranslation();

  const renderField = (label: string, name: string, isPercent = false) => (
    <div key={name} className="flex items-center gap-2">
      <label className="w-60 text-gray-700 font-medium text-xs">{label}</label>
      <input
        type="text"
        value={
          isPercent
            ? formatPercent((project as any)?.[name] as number)
            : formatNumber((project as any)?.[name] as number)
        }
        disabled
        className="border rounded p-1 text-xs w-20 bg-gray-100 text-right"
      />
    </div>
  );

  const renderCalculatedField = (label: string, value: number, isPercent = false) => (
    <div key={label} className="flex items-center gap-2">
      <label className="w-60 text-gray-700 font-medium text-xs">{label}</label>
      <input
        type="text"
        value={isPercent ? formatPercent(value) : formatNumber(value)}
        disabled
        className="border rounded p-1 text-xs w-20 bg-gray-100 text-right"
      />
    </div>
  );

  if (loading) return <div className="p-4 text-xs">{t("common.loading")}</div>;
  if (error || !project) return <div className="p-4 text-elani-orange text-xs">{t("common.error_loading_data")}</div>;

  // --- Frontend calculations (based on provider project) ---
  const p: any = project;
  const lifetime = (formData as any)?.lifetime ?? p.lifetime ?? 0;
  const capacity = p.capacity ?? 0;
  const relativeCosts = p.relative_costs ?? 0;
  const brokerCommission = p.broker_commission ?? 0;
  const debtShare = p.debt_ratio ?? 0;
  const taxRate = p.tax_rate ?? 0;
  const iabRate = p.investment_deduction_amount_iab ?? 0;
  const specialDepYear1 = p.special_depreciation_year_1 ?? 0;
  const specialDepYear2 = p.special_depreciation_year_2 ?? 0;
  const specialDepYear3 = p.special_depreciation_year_3 ?? 0;
  const specialDepYear4 = p.special_depreciation_year_4 ?? 0;
  const specialDepYear5 = p.special_depreciation_year_5 ?? 0;
  const specialDepTotalRate =
    specialDepYear1 + specialDepYear2 + specialDepYear3 + specialDepYear4 + specialDepYear5;
  const depreciationPeriod = p.depreciation_period ?? 0;

  // Prefer backend-calculated initial investment if available (Investment (EUR) in Year 1 is a negative outflow)
  const investmentResults: any[] = (results as any)?.investment ?? [];
  const initialInvestmentFromBackendRaw: number =
    investmentResults?.length > 0 ? (investmentResults[0]?.["Investment (EUR)"] as number) ?? 0 : 0;
  
  // Unified total investment used for display & tax calc
  const totalInvestment = Math.abs(initialInvestmentFromBackendRaw);

  const nominalDebtFromCalc = (totalInvestment * (1 - brokerCommission)) * debtShare;
  const disagioRate = p.debt_disagio ?? 0;
  const disagioFromCalc = nominalDebtFromCalc * disagioRate;
  const loanProceedsFromCalc = nominalDebtFromCalc - disagioFromCalc;
  const equity = totalInvestment - loanProceedsFromCalc;

  const debtRows: any[] = (results as any)?.debt ?? [];
  const netDebtRows: any[] = (results as any)?.net_debt ?? [];
  const disagioRows: any[] = (results as any)?.disagio ?? [];
  const drawYearDebt = debtRows.find((row) => (row["Debt (EUR)"] ?? 0) > 0);
  const drawYearNetDebt = netDebtRows.find((row) => (row["Net Debt (EUR)"] ?? 0) > 0);
  const drawYearDisagio = disagioRows.find((row) => (row["Disagio (EUR)"] ?? 0) > 0);
  const nominalDebt = drawYearDebt?.["Debt (EUR)"] ?? nominalDebtFromCalc;
  const loanProceeds = drawYearNetDebt?.["Net Debt (EUR)"] ?? loanProceedsFromCalc;
  const disagioAmount = drawYearDisagio?.["Disagio (EUR)"] ?? disagioFromCalc;

  const iabTax = totalInvestment * iabRate * taxRate;
  const specialTax = totalInvestment * (1 - iabRate) * specialDepYear1 * taxRate;
  const residualBase = totalInvestment * (1 - iabRate) * (1 - specialDepTotalRate);
  const linearTax = depreciationPeriod > 0 ? (residualBase / depreciationPeriod) * taxRate : 0;
  const taxTotal = iabTax + specialTax + linearTax;

  const roi20Label = lifetime >= 20 ? "ROI (avg 20y)" : `ROI (avg ${lifetime}y)`;


  return (
    <div className="overflow-x-auto max-w-6xl text-xs space-y-8">

      <div className="bg-white rounded-xl shadow-sm border border-border p-6">
        <form className="max-w-md space-y-6 text-xs">
          <div>
            <h2 className="text-base font-bold mb-2">{t("project.results.key_metrics")}</h2>
            <div className="space-y-2">
              {renderField(t("project.results.operating_profit_margin"), "operating_profit_margin", true)}
              {renderField(roi20Label, "roi_avg_20y", true)}
              {renderField(t("project.results.roi_avg_lifetime"), "roi_avg_lifetime", true)}
              {renderField(t("project.results.irr_project"), "irr_project_unlevered_pre_tax", true)}
              {renderField(t("project.results.irr_equity_levered_pre_tax"), "irr_equity_levered_pre_tax", true)}
              {renderField(t("project.results.irr_equity_levered_post_tax"), "irr_equity_levered_post_tax", true)}
              {renderField(t("project.results.payback_period") + " (" + t("units.years") + ")", "payback_period")}
            </div>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-border p-6">
        <form className="max-w-md space-y-6 text-xs">
          <div>
            <h2 className="text-base font-bold mb-2">{t("project.results.investment_and_financing")}</h2>
            <div className="space-y-2">
              {renderCalculatedField(t("project.results.initial_investment") + " (" + t("units.EUR") + ")", totalInvestment)}
              {renderCalculatedField(t("project.results.debt_nominal") + " (" + t("units.EUR") + ")", nominalDebt)}
              {disagioAmount > 0 && renderCalculatedField(t("project.results.disagio") + " (" + t("units.EUR") + ")", disagioAmount)}
              {disagioAmount > 0 && renderCalculatedField(t("project.results.loan_proceeds") + " (" + t("units.EUR") + ")", loanProceeds)}
              {renderCalculatedField(t("project.results.equity") + " (" + t("units.EUR") + ")", equity)}
            </div>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-border p-6">
        <form className="max-w-md space-y-6 text-xs">
          <div>
            <h2 className="text-base font-bold mb-2">{t("project.results.tax_savings")}</h2>
            <div className="space-y-2">
              {renderCalculatedField(t("project.results.iab") + " (" + t("units.EUR") + ")", iabTax)}
              {renderCalculatedField(t("project.results.special_depreciation") + " (" + t("units.EUR") + ")", specialTax)}
              {renderCalculatedField(t("project.results.remaining_depreciation") + " (" + t("units.EUR") + ")", linearTax)}
              {renderCalculatedField(t("project.results.total") + " (" + t("units.EUR") + ")", taxTotal)}
            </div>
          </div>
        </form>
      </div>

    </div>
    
  );
}